import React from 'react';
import { Box, Typography, darken, lighten } from '@mui/material';
import GaugeComponent from 'react-gauge-component';
import { WidgetContent } from 'src/components/Widget/types';
import { DashboardDataStatus } from 'src/api/services/types';
import {
  GaugeColor,
  getGaugeColor,
  getScoreText,
  getScoreTextSmiley,
  getViolationsText,
  getVisiblyCorrectValue,
} from './utils';

const AccountStatusContent: WidgetContent<
  Pick<DashboardDataStatus, 'score' | 'violatingCount'>
> = ({ data }) => {
  const score = data?.score ?? 0;
  const formattedScore = +(score * 100).toFixed(0);
  const violatingCount = data?.violatingCount ?? null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      width="120%"
      height="100%"
      sx={{
        ['& .value-text, & .tick-line, & .tick-value']: {
          display: 'none',
        },
        ['& circle']: {
          stroke: darken(getGaugeColor(formattedScore), 0.3),
        },
        ['& .semicircle-gauge']: {
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        },
      }}
      justifyContent="center"
      alignItems="center"
    >
      <GaugeComponent
        value={getVisiblyCorrectValue(formattedScore)}
        type="semicircle"
        marginInPercent={{ top: 0.04, bottom: 0.0, left: 0.035, right: 0.035 }}
        pointer={{
          type: 'blob',
          animationDelay: 0,
          animationDuration: 1000,
          width: 14,
        }}
        style={{
          transform: 'rotateY(180deg)',
        }}
        arc={{
          colorArray: Object.values(GaugeColor),
          subArcs: [
            { length: 50 },
            { length: 20 },
            { length: 15 },
            { length: 15 },
          ],
          padding: 0.015,
          width: 0.115,
        }}
      />
      <Box
        display="flex"
        flexWrap="nowrap"
        flexDirection="column"
        alignItems="center"
        letterSpacing={-0.5}
        sx={{
          position: 'relative',
        }}
      >
        <Box
          display="flex"
          flexWrap="nowrap"
          gap={0.5}
          sx={{
            background: ({ palette }) =>
              lighten(palette.primaryLight80 ?? '', 0.5),
            borderRadius: '10px',
            padding: ({ spacing }) => spacing(0, 1.5, 0, 1),
          }}
        >
          <Typography color="primary" sx={{ fontSize: 20 }}>
            המצב שלך
          </Typography>
          <Box display="flex" flexWrap="nowrap" gap={0.25}>
            <Typography
              fontWeight="700"
              color="primary"
              sx={{ fontSize: 20, display: 'inline' }}
            >
              {getScoreText(formattedScore)}
            </Typography>
            <Typography
              fontWeight="700"
              sx={{ fontSize: 20, display: 'inline' }}
            >
              {getScoreTextSmiley(formattedScore)}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          letterSpacing={-0.25}
          fontSize={14}
          sx={{ opacity: 0.75 }}
        >
          {getViolationsText(violatingCount ?? 0)}
        </Typography>
        <Typography
          variant="h1"
          fontWeight={650}
          color="primary"
          letterSpacing={-1}
          sx={{
            fontSize: 48,
            position: 'absolute',
            left: '50%',
            top: '-100%',
            fontFamily: 'Inter',
            transform: 'translate(-50%,-50%)',
          }}
        >{`${formattedScore}%`}</Typography>
      </Box>
    </Box>
  );
};

export default AccountStatusContent;
